import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toUpper } from "lodash";
import Column from "antd/lib/table/Column";
import moment from "moment";
import { Table, Tag, Button, Checkbox } from "antd";
import { useDispatch } from "react-redux";
import StarOutlined from "@ant-design/icons/es/icons/StarOutlined";
import StarFilled from "@ant-design/icons/es/icons/StarFilled";
import Localize from "../../libs/localization";
import useListSelection from "../../hooks/useListSelection";
import { deleteAssociatedIdTagAction } from "../../actions/tag.actions";
import GatedTableEmptyState from "../../design-library/GatedTableEmptyState/GatedTableEmptyState.component";

const PitchScoreList = (props) => {
  const {
    data,
    total,
    loadingList,
    options,
    setOptions,
    tagMode = false,
    setSelectedPitchScores = () => {},
    loadingTag = false,
    associatedTagsIds = [],
    onTagRemove = () => {},
    hasAccess = true,
  } = props;

  const { page, pageSize } = options;
  const [selection, setSelection] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const dispatch = useDispatch();

  const { deselectAll, addToSelection, removeFromSelection } = useListSelection(
    {
      selection,
      setSelection,
    },
  );

  useEffect(() => {
    setSelectedPitchScores(selection);
  }, [selection]);

  useEffect(() => {
    deselectAll();
    setSelectAll(false);
  }, [tagMode]);

  const sdMap = {
    asc: "ascend",
    desc: "descend",
  };

  const renderTag = (item) => {
    let renderedTags = [];
    associatedTagsIds &&
      associatedTagsIds.map((association) => {
        const founded = association._id === item._id;
        if (founded) {
          renderedTags = association.tags.map((tag) => (
            <Tag
              key={tag.tagId}
              color={`#${tag.color}`}
              className="searchPage__item-tag ml-2"
              closable
              onClose={(e) => {
                e.stopPropagation();
                dispatch(
                  deleteAssociatedIdTagAction(
                    {
                      tagId: tag.tagId,
                      associatedId: association._id,
                    },
                    onTagRemove,
                  ),
                );
              }}
            >
              {tag.name}
            </Tag>
          ));
        }
      });
    return renderedTags;
  };

  const handleToggleSelection = (pitchScoreId) => {
    const currentSelections = selection || [];
    if (currentSelections.includes(pitchScoreId)) {
      removeFromSelection([pitchScoreId]);
    } else {
      addToSelection([pitchScoreId]);
    }
  };

  const renderStar = (pitchScoreId) => {
    const isSelected = selection.includes(pitchScoreId);
    const Component = isSelected ? StarFilled : StarOutlined;
    return <Component style={{ fontSize: 20 }} />;
  };

  return (
    <>
      {tagMode && (
        <div style={{ marginBottom: 16 }}>
          <Checkbox
            checked={selectAll}
            onChange={(e) => {
              setSelectAll(e.target.checked);
              const ids = data.map((item) => item._id);
              if (e.target.checked) {
                addToSelection(ids);
              } else {
                removeFromSelection(ids);
              }
            }}
          >
            {Localize("COMMON.SELECT_ALL")}
          </Checkbox>
        </div>
      )}
      <Table
        loading={loadingList}
        scroll={{ x: 1000 }}
        locale={
          !hasAccess
            ? {
                emptyText: (
                  <GatedTableEmptyState
                    message={Localize("PAGES.PITCH_SCORES.GATED_MESSAGE")}
                  />
                ),
              }
            : undefined
        }
        onChange={(pagination, filters, sorter, extra) => {
          if (extra.action === "sort") {
            if (sorter.order) {
              setOptions({
                ...options,
                sortBy: sorter.columnKey,
                sortDirection: sorter.order === "ascend" ? "asc" : "desc",
              });
            } else {
              setOptions({
                ...options,
                sortBy: undefined,
                sortDirection: undefined,
              });
            }
          }
        }}
        dataSource={data.map((o) => {
          return {
            ...o,
            key: o._id,
            company: o.company,
            category: o.category?.key || o._id?.category?.id,
            endDate: moment(o.latestEndDate).format("DD/MM/YYYY"),
            scope: o.scope,
            currentAgency: o.latestPitch?.winnerCompany,
            contractScore: `${o.score}%`,
          };
        })}
        pagination={{
          total,
          pageSize,
          current: page,
          onChange: (page_, pageSize_) => {
            setOptions({ ...options, page: page_, pageSize: pageSize_ });
          },
        }}
      >
        {tagMode && (
          <Column
            title=""
            width={60}
            render={(_, record) => (
              <Button
                type="text"
                shape="circle"
                size="small"
                onClick={() => handleToggleSelection(record._id)}
              >
                {renderStar(record._id)}
              </Button>
            )}
            key="selection"
          />
        )}
        {tagMode && (
          <Column
            title={
              <span style={{ fontWeight: "bold" }}>
                {Localize("COMMON.TAGS")}
              </span>
            }
            width={200}
            render={(_, record) => <div>{renderTag(record)}</div>}
            key="tags"
          />
        )}
        <Column
          title={
            <span style={{ fontWeight: "bold" }}>
              {Localize("PAGES.PITCH_SCORES.FILTERS.COMPANY")}
            </span>
          }
          dataIndex="company"
          render={(company) => {
            return (
              <Link to={`/companies/${company?.id}`}>
                {toUpper(company?.name || company?.id)}
              </Link>
            );
          }}
          key="company"
          sortDirections={["ascend", "descend"]}
          sorter
          sortOrder={
            options.sortBy === "company" && sdMap[options.sortDirection]
          }
        />
        <Column
          title={
            <span style={{ fontWeight: "bold" }}>
              {Localize("PAGES.PITCH_SCORES.FILTERS.CATEGORY")}
            </span>
          }
          dataIndex="category"
          key="category"
          sortDirections={["ascend", "descend"]}
          sorter
          sortOrder={
            options.sortBy === "category" && sdMap[options.sortDirection]
          }
        />
        <Column
          title={
            <span style={{ fontWeight: "bold" }}>
              {Localize("PAGES.PITCH_SCORES.FILTERS.END_DATE")}
            </span>
          }
          dataIndex="endDate"
          key="endDate"
          sortDirections={["ascend", "descend"]}
          sorter
          sortOrder={
            options.sortBy === "endDate" && sdMap[options.sortDirection]
          }
        />
        <Column
          title={
            <span style={{ fontWeight: "bold" }}>
              {Localize("PAGES.PITCH_SCORES.FILTERS.GEO_AREA")}
            </span>
          }
          dataIndex="scope"
          key="scope"
          sortDirections={["ascend", "descend"]}
          sorter
          sortOrder={options.sortBy === "scope" && sdMap[options.sortDirection]}
        />
        <Column
          title={
            <span style={{ fontWeight: "bold" }}>
              {Localize("PAGES.PITCH_SCORES.FILTERS.AGENCY")}
            </span>
          }
          dataIndex="currentAgency"
          render={(currentAgency) => {
            if (currentAgency?.name) {
              return (
                <Link to={`/companies/${currentAgency?.id}`}>
                  {toUpper(currentAgency?.name || currentAgency?.id)}
                </Link>
              );
            }
            return null;
          }}
          key="currentAgency"
        />
        <Column
          title={
            <span style={{ fontWeight: "bold" }}>
              {Localize("PAGES.PITCH_SCORES.FILTERS.CONTRACT_SCORE")}
            </span>
          }
          dataIndex="contractScore"
          key="score"
          sortDirections={["ascend", "descend"]}
          sorter
          sortOrder={options.sortBy === "score" && sdMap[options.sortDirection]}
        />
        {!tagMode && (
          <Column
            title={
              <span style={{ fontWeight: "bold" }}>
                {Localize("COMMON.TAGS")}
              </span>
            }
            width={200}
            render={(_, record) => <div>{renderTag(record)}</div>}
            key="tags"
          />
        )}
      </Table>
    </>
  );
};

export default PitchScoreList;
